// Chat mode's queries (M2-06): one place for keys and SDK calls. Every key sits under
// `["company", co, "chat", …]`, so a change ping for a channel, topic or message (RT-01,
// live/change-pings.ts) refetches what the chat screens show and nothing else. Task, approval
// and run cards keep their own subjects' keys.
import type { OperationOutput } from "@companyos/sdk";
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import type { Api } from "../../api/client";

export type Space = OperationOutput<"spaces.get">;
export type SidebarChannel = OperationOutput<"channels.list">["data"][number];
export type Channel = OperationOutput<"channels.get">;
export type TopicSummary = OperationOutput<"channels.topics">["data"][number];
export type TopicDetail = OperationOutput<"topics.get">;
export type TopicMember = OperationOutput<"topics.members">["data"][number];
export type MessageView = OperationOutput<"messages.list">["data"][number];
export type ChannelPrefs = OperationOutput<"channels.setPrefs">;
export type AgentRules = OperationOutput<"channels.agentRules">;
export type Follow = OperationOutput<"follows.put">;
export type Bookmark = OperationOutput<"bookmarks.list">["data"][number];
export type Board = OperationOutput<"boards.get">;
export type TaskDetail = OperationOutput<"tasks.get">;
// the approval row; `approvals.get` adds the caller's `can_view` / `can_decide` (Parity P6a)
export type Approval = OperationOutput<"approvals.approve">;
export type Run = OperationOutput<"runs.list">["data"][number];
export type Peer = OperationOutput<"presence.get">["peers"][number];
export type ThreadSummaryView = OperationOutput<"threads.mine">["data"][number];
export type ChannelMember = OperationOutput<"channels.members">["data"][number];
export type AgentRoomView = OperationOutput<"agentRooms.get">;
export type RoomParticipant = AgentRoomView["participants"][number];
export type StatusAsk = OperationOutput<"statusAsks.answer">;
/** AGENT-MENTION: what became of an agent a message asked; who a mention here would wake. */
export type AgentWake = OperationOutput<"messages.wake">;
export type ThreadAgents = OperationOutput<"threads.agents">;
export type AgentReadiness = ThreadAgents["data"][number];
/** COS-SLICE1: a follow-up card under a message ("Make this a task?"). */
export type Suggestion = OperationOutput<"suggestions.list">["data"][number];

const root = (co: string) => ["company", co, "chat"] as const;

export const chatKeys = {
  root,
  spaces: (co: string) => [...root(co), "spaces"] as const,
  channels: (co: string) => [...root(co), "channels"] as const,
  prefs: (co: string) => [...root(co), "prefs"] as const,
  channel: (co: string, id: string) => [...root(co), "channel", id] as const,
  topics: (co: string, channel: string) => [...root(co), "topics", channel] as const,
  topic: (co: string, id: string) => [...root(co), "topic", id] as const,
  /** CHAT-TOPICS-SEPARATE: a topic's members (under `topic`, so its invalidations reach it). */
  topicMembers: (co: string, id: string) => [...root(co), "topic", id, "members"] as const,
  messages: (co: string, thread: string) => [...root(co), "messages", thread] as const,
  /** CHAT-CONTROLS: the caller's threads (the Threads view and its sidebar count). */
  threads: (co: string) => [...root(co), "threads"] as const,
  rules: (co: string, channel: string) => [...root(co), "rules", channel] as const,
  members: (co: string, channel: string) => [...root(co), "members", channel] as const,
  follows: (co: string) => [...root(co), "follows"] as const,
  bookmarks: (co: string) => [...root(co), "bookmarks"] as const,
  boards: (co: string) => [...root(co), "boards"] as const,
  presence: (co: string) => [...root(co), "presence"] as const,
  room: (co: string, channel: string) => [...root(co), "room", channel] as const,
  asks: (co: string, room: string) => [...root(co), "asks", room] as const,
  /** COS-SLICE1: a thread's pending follow-up cards (people only; agents get none). */
  suggestions: (co: string, thread: string) => [...root(co), "suggestions", thread] as const,
  /** AGENT-MENTION: a thread's agents and their readiness (the @ menu, "Add to #channel"). */
  agents: (co: string, thread: string) => ["company", co, "agent-readiness", thread] as const,
  // Cards follow their own subjects (task, approval, run pings).
  task: (co: string, id: string) => ["company", co, "task", id] as const,
  approval: (co: string, id: string) => ["company", co, "approval", id] as const,
  runs: (co: string) => ["company", co, "runs", "chat"] as const,
  /** CHAT-SWITCH-B: where a linked message is, with its root and replies. */
  locate: (co: string, id: string) => [...root(co), "locate", id] as const,
  /** CHAT-SWITCH-B: the viewer's scheduled messages. */
  scheduled: (co: string) => [...root(co), "scheduled"] as const,
  /** CHAT-SWITCH-B: a message's link cards. */
  previews: (co: string, message: string) => [...root(co), "previews", message] as const,
  /** CHAT-SWITCH-B: a channel's incoming webhooks (its managers). */
  webhooks: (co: string, channel: string) => [...root(co), "webhooks", channel] as const,
  /** CHAT-SWITCH-B: one of the viewer's asks. */
  ask: (co: string, id: string) => [...root(co), "ask", id] as const,
};

/** CHAT-SWITCH-B: a message link's place, its thread root and the root's replies. */
export const locateQuery = (api: Api, co: string, id: string) =>
  queryOptions({
    queryKey: chatKeys.locate(co, id),
    queryFn: () => api.client.messages.locate({ params: { co, id } }),
    retry: false,
    staleTime: 15_000,
  });

/** Lists the screens read whole: the API's page size cap, one call each. */
const ALL = { limit: 200 } as const;

export const spacesQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: chatKeys.spaces(co),
    queryFn: () => api.client.spaces.list({ params: { co }, query: ALL }),
  });

/**
 * MULTI-SPACE-ACCESS: every channel share the caller can see (the sidebar's shared rows, the
 * Spaces tab). Under `channels`, so a channel write's invalidation refreshes it.
 */
export const channelSharesQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: [...chatKeys.channels(co), "shares"] as const,
    queryFn: () => api.client.channels.shares.list({ params: { co }, query: {} }),
  });

/**
 * Every channel the caller may see, archived ones included (CHAT-CONTROLS: one cached list
 * serves the sidebar, Browse and Archived channels; `sidebarSections` and Browse leave the
 * archived out, the Archived page keeps only them).
 */
export const channelsQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: chatKeys.channels(co),
    // WF-FIX (2026-10-26): every page (a company past 200 channels lost the rest).
    queryFn: () =>
      everyPage((cursor) =>
        api.client.channels.list({
          params: { co },
          query: { ...ALL, include_archived: "true" as const, ...(cursor ? { cursor } : {}) },
        }),
      ),
  });

/**
 * WF-FIX (2026-10-26): follows `next_cursor` to the last page and returns one page holding every
 * row (the screens read these lists whole; one call while a list fits a page).
 */
async function everyPage<P extends { data: unknown[]; next_cursor: string | null }>(
  fetch: (cursor: string | undefined) => Promise<P>,
): Promise<P> {
  const first = await fetch(undefined);
  const data = [...first.data];
  let cursor = first.next_cursor ?? undefined;
  while (cursor) {
    const next = await fetch(cursor);
    data.push(...next.data);
    cursor = next.next_cursor ?? undefined;
  }
  return { ...first, data, next_cursor: null };
}

/** CHAT-CONTROLS: the caller's threads, latest reply first (Slack's Threads view). */
export const myThreadsQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: chatKeys.threads(co),
    queryFn: () => api.client.threads.mine({ params: { co }, query: ALL }),
  });

/**
 * The caller's channel settings. Until the backend serves them (M2-02), a refused call reads as
 * "no settings yet": every channel the caller sees counts as joined, nothing is muted.
 */
export const prefsQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: chatKeys.prefs(co),
    queryFn: () => api.client.channels.myPrefs({ params: { co }, query: ALL }),
  });

export const channelQuery = (api: Api, co: string, id: string) =>
  queryOptions({
    queryKey: chatKeys.channel(co, id),
    queryFn: () => api.client.channels.get({ params: { co, id } }),
  });

/**
 * A channel's topics, archived ones included (CHAT-UX): the server adds those for the
 * channel's managers only and leaves them out for everyone else, so one cached list serves
 * the sidebar, the stream and the "Archived (n)" group (`orderedTopics` / `archivedTopics`
 * split it). CHAT-TOPICS-SEPARATE: every page — the channel's own conversation is a topic made
 * on its first post, so it can sit past the first page.
 */
export const topicsQuery = (api: Api, co: string, channel: string) =>
  queryOptions({
    queryKey: chatKeys.topics(co, channel),
    queryFn: () =>
      everyPage((cursor) =>
        api.client.channels.topics({
          params: { co, id: channel },
          query: { ...ALL, include_archived: "true" as const, ...(cursor ? { cursor } : {}) },
        }),
      ),
  });

export const topicQuery = (api: Api, co: string, id: string) =>
  queryOptions({
    queryKey: chatKeys.topic(co, id),
    queryFn: () => api.client.topics.get({ params: { co, id } }),
  });

/** CHAT-TOPICS-SEPARATE: a topic's members (the whole list; topics stay small). */
export const topicMembersQuery = (api: Api, co: string, id: string) =>
  queryOptions({
    queryKey: chatKeys.topicMembers(co, id),
    queryFn: () => api.client.topics.members({ params: { co, id } }),
  });

/** A topic's messages, newest page first; older pages through `before` (the `next_cursor`). */
export const messagesQuery = (api: Api, co: string, thread: string, limit = 100) =>
  infiniteQueryOptions({
    queryKey: [...chatKeys.messages(co, thread), limit] as const,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.client.messages.list({
        params: { co, id: thread },
        query: { limit, ...(pageParam ? { before: pageParam } : {}) },
      }),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });

/** The channel stream's page of a topic: its latest 30 messages (CHAT-UX interleaved view). */
export const latestMessagesQuery = (api: Api, co: string, thread: string) =>
  queryOptions({
    queryKey: [...chatKeys.messages(co, thread), "latest"] as const,
    queryFn: () => api.client.messages.list({ params: { co, id: thread }, query: { limit: 30 } }),
  });

/** A channel's member rows (the header's count, the Members dialog). */
export const membersQuery = (api: Api, co: string, channel: string) =>
  queryOptions({
    queryKey: chatKeys.members(co, channel),
    // WF-FIX (2026-10-26): every page (the count, the pickers' "already in" and the list).
    queryFn: () =>
      everyPage((cursor) =>
        api.client.channels.members({
          params: { co, id: channel },
          query: { ...ALL, ...(cursor ? { cursor } : {}) },
        }),
      ),
  });

/**
 * AGENT-MENTION: the agents a mention in this thread reaches and whether each would wake now.
 * Its own key root (`agent-readiness`): channel, DE and membership pings refetch it
 * (live/change-pings.ts), without refetching the conversation.
 */
export const threadAgentsQuery = (api: Api, co: string, thread: string, root?: string) =>
  queryOptions({
    // THREAD-WAKE: a root's reply thread is its own conversation (its own addressee), under
    // the thread's key so the thread's invalidations reach it.
    queryKey: root ? [...chatKeys.agents(co, thread), root] : chatKeys.agents(co, thread),
    queryFn: () =>
      api.client.threads.agents({
        params: { co, id: thread },
        ...(root ? { query: { root } } : {}),
      }),
    staleTime: 30_000,
  });

export const rulesQuery = (api: Api, co: string, channel: string) =>
  queryOptions({
    queryKey: chatKeys.rules(co, channel),
    queryFn: () => api.client.channels.agentRules({ params: { co, id: channel } }),
  });

export const followsQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: chatKeys.follows(co),
    queryFn: () => api.client.follows.mine({ params: { co }, query: ALL }),
  });

export const bookmarksQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: chatKeys.bookmarks(co),
    queryFn: () => api.client.bookmarks.list({ params: { co }, query: ALL }),
  });

export const boardsQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: chatKeys.boards(co),
    queryFn: () => api.client.boards.list({ params: { co }, query: ALL }),
    staleTime: 5 * 60_000,
  });

export const suggestionsQuery = (api: Api, co: string, threadId: string) =>
  queryOptions({
    queryKey: chatKeys.suggestions(co, threadId),
    queryFn: () => api.client.suggestions.list({ params: { co, id: threadId } }),
  });

export const taskQuery = (api: Api, co: string, id: string) =>
  queryOptions({
    queryKey: chatKeys.task(co, id),
    queryFn: () => api.client.tasks.get({ params: { co, id } }),
    retry: false,
  });

export const approvalQuery = (api: Api, co: string, id: string) =>
  queryOptions({
    queryKey: chatKeys.approval(co, id),
    queryFn: () => api.client.approvals.get({ params: { co, id } }),
    retry: false,
  });

/** Recent runs (one call): the cost chips, the status line and the agent room's states. */
export const runsQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: chatKeys.runs(co),
    queryFn: () => api.client.runs.list({ params: { co }, query: ALL }),
  });

/** A channel's agent room (M4-05): participants with badges, whose turn it is. Null when none. */
export const roomQuery = (api: Api, co: string, channel: string) =>
  queryOptions({
    queryKey: chatKeys.room(co, channel),
    queryFn: async () =>
      (await api.client.agentRooms.list({ params: { co }, query: { channel_id: channel } }))
        .data[0] ?? null,
  });

/** A room's status asks still waiting on a person (escalated ones). */
export const asksQuery = (api: Api, co: string, room: string) =>
  queryOptions({
    queryKey: chatKeys.asks(co, room),
    queryFn: () =>
      api.client.statusAsks.list({ params: { co, id: room }, query: { state: "escalated" } }),
  });

export const presenceQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: chatKeys.presence(co),
    queryFn: () => api.client.presence.get({ params: { co } }),
    refetchInterval: 30_000,
  });

/** CHAT-SWITCH-A: one person's presence, custom status, time zone and do-not-disturb. */
export type ChatStatus = OperationOutput<"chatStatus.list">["data"][number];

/**
 * CHAT-SWITCH-A: everyone's status the caller can see (the presence dots, status emoji, local
 * time): every minute and on focus. Its own key root, so chat pings do not refetch it.
 */
export const statusesQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: ["company", co, "chat-statuses"] as const,
    queryFn: () => api.client.chatStatus.list({ params: { co } }),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
    retry: false,
  });

/** A fresh idempotency id for `client_id` (04: idempotent per company forever). */
export function clientId(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `c-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
